/* eslint-disable */
import { useState } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import QuizIcon from '@mui/icons-material/Quiz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import {
  SeoPage,
  SeoTrackedKeyword,
  SeoAhrefsDomainSnapshot,
  SeoAhrefsKeywordCache,
  SeoCopilotSession,
  WpAppliedChange,
} from '@/types/api/seo';
import { PageHeader } from '@/components/shared';
import { ServicePageHealthResult } from '@/services/seoAi';
import ChangeImpactTimeline from '@/components/ChangeImpactTimeline';

type Props = {
  page: SeoPage | null;
  isLoading: boolean;
  projectId: string;
  keywords: SeoTrackedKeyword[];
  difficultyMap: Map<string, SeoAhrefsKeywordCache>;
  ahrefsDomainSnapshot: SeoAhrefsDomainSnapshot | null;
  ahrefsNewBacklinks: number;
  ahrefsLostBacklinks: number;
  appliedChanges: WpAppliedChange[];
  copilotByKeyword: Map<string, SeoCopilotSession>;
  isServicePage: boolean;
  healthResult: ServicePageHealthResult | null;
  isCheckingHealth: boolean;
  onCheckHealth: () => void;
  isRunningCopilot: boolean;
  copilotResult: string | null;
  onRunCopilot: () => void;
  fixingCheck: string | null;
  onFixCheck: (checkKey: string) => void;
  // FAQ preview workflow
  generatedFaqs: Array<{ question: string; answer: string }> | null;
  generatedFaqSchema: string | null;
  isGeneratingFaqs: boolean;
  isPublishingFaqs: boolean;
  onGenerateFaqs: () => void;
  onPublishFaqs: () => void;
  onClearFaqPreview: () => void;
};

const fmtNum = (n: number): string => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const positionColor = (pos: number | null | undefined): string => {
  if (pos == null) return '#96979b';
  if (pos <= 3) return '#0D9D58';
  if (pos <= 10) return '#4285F4';
  if (pos <= 20) return '#F4B400';
  return '#EA4335';
};

const kdColor = (kd: number | null | undefined): string => {
  if (kd == null) return '#96979b';
  if (kd < 30) return '#0D9D58';
  if (kd < 60) return '#F4B400';
  return '#EA4335';
};

const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  p: 2.5,
  backgroundColor: '#fff',
  textAlign: 'center' as const,
  flex: 1,
  minWidth: 120,
};

const sectionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  p: 3,
  backgroundColor: '#fff',
  mb: 4,
};

const formatPath = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const SeoPageDetailComponent = function ({
  page,
  isLoading,
  projectId,
  keywords,
  difficultyMap,
  ahrefsDomainSnapshot,
  ahrefsNewBacklinks,
  ahrefsLostBacklinks,
  appliedChanges,
  copilotByKeyword,
  isServicePage,
  healthResult,
  isCheckingHealth,
  onCheckHealth,
  isRunningCopilot,
  copilotResult,
  onRunCopilot,
  fixingCheck,
  onFixCheck,
  generatedFaqs,
  generatedFaqSchema,
  isGeneratingFaqs,
  isPublishingFaqs,
  onGenerateFaqs,
  onPublishFaqs,
  onClearFaqPreview,
}: Props): JSX.Element {
  const basePath = `/seox/projects/${projectId}`;
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Debug logging
  console.log(
    '[PageDetail] isServicePage=',
    isServicePage,
    'healthResult=',
    !!healthResult,
    'keywords=',
    keywords.length,
    'generatedFaqs=',
    generatedFaqs?.length ?? 0
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  if (!page) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Page not found.
        </Typography>
      </Box>
    );
  }

  // Compute aggregate metrics
  const positions = keywords
    .map((k) => k.latestSnapshot?.position)
    .filter((p): p is number => p != null && p < 999);
  const bestPosition = positions.length > 0 ? Math.min(...positions) : null;
  const totalClicks = keywords.reduce((s, k) => s + (k.latestSnapshot?.clicks ?? 0), 0);
  const totalImpressions = keywords.reduce((s, k) => s + (k.latestSnapshot?.impressions ?? 0), 0);
  const kds = keywords
    .map((k) => difficultyMap.get(k.keyword.toLowerCase())?.difficulty)
    .filter((d): d is number => d != null);
  const avgKd = kds.length > 0 ? Math.round(kds.reduce((s, v) => s + v, 0) / kds.length) : null;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title={formatPath(page.url)}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isServicePage && (
              <Chip
                label="Service Page"
                size="small"
                sx={{ fontWeight: 500, backgroundColor: '#054AD910', color: '#054AD9' }}
              />
            )}
            <a href={page.url} target="_blank" rel="noopener noreferrer">
              <Chip
                icon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                label="Open Page"
                size="small"
                variant="outlined"
                clickable
                sx={{ fontSize: 11 }}
              />
            </a>
            {keywords.length > 0 && (
              <Button
                variant="contained"
                startIcon={
                  isRunningCopilot ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : (
                    <RocketLaunchIcon sx={{ fontSize: 16 }} />
                  )
                }
                onClick={onRunCopilot}
                disabled={isRunningCopilot}
                sx={{
                  backgroundColor: '#054AD9',
                  '&:hover': { backgroundColor: '#0440B8' },
                  textTransform: 'none',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {isRunningCopilot ? 'Running...' : 'Run Copilot'}
              </Button>
            )}
          </Box>
        }
      />

      {/* Copilot result banner */}
      {copilotResult && (
        <Box sx={{ mb: 3, p: 1.5, backgroundColor: '#E6F4EA', borderRadius: 1 }}>
          <Typography variant="body2" color="#0D9D58" fontWeight={500}>
            {copilotResult}. Check the keyword detail pages for generated tasks.
          </Typography>
        </Box>
      )}

      {/* Title & Meta */}
      {(page.title || page.metaDescription) && (
        <Box sx={{ mb: 3, p: 2, borderRadius: 1.5, backgroundColor: '#F4F4F5' }}>
          {page.title && (
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {page.title}
            </Typography>
          )}
          {page.metaDescription && (
            <Typography variant="caption" color="text.secondary">
              {page.metaDescription}
            </Typography>
          )}
        </Box>
      )}

      {/* Metrics row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Keywords
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#054AD9">
            {keywords.length}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
            targeting this page
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Best Position
          </Typography>
          <Typography variant="h4" fontWeight={700} color={positionColor(bestPosition)}>
            {bestPosition != null ? `#${bestPosition.toFixed(1)}` : '—'}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Clicks
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#4285F4">
            {totalClicks > 0 ? fmtNum(totalClicks) : '—'}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Impressions
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#A142F4">
            {totalImpressions > 0 ? fmtNum(totalImpressions) : '—'}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Avg KD
          </Typography>
          <Typography variant="h4" fontWeight={700} color={kdColor(avgKd)}>
            {avgKd ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
            {avgKd != null ? (avgKd < 30 ? 'Easy' : avgKd < 60 ? 'Medium' : 'Hard') : 'Ahrefs'}
          </Typography>
        </Box>
        <Box sx={cardSx}>
          <Typography variant="caption" color="text.secondary">
            Domain Rating
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#054AD9">
            {ahrefsDomainSnapshot?.domainRating ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
            / 100
          </Typography>
        </Box>
      </Box>

      {/* Keywords Table */}
      <Box sx={sectionSx}>
        <Typography
          variant="overline"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}
        >
          Keywords Targeting This Page
        </Typography>
        {keywords.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No keywords targeting this page yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    Keyword
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    Position
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    KD
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    Clicks
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    Impressions
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    CTR
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#71717A',
                      fontSize: 12,
                    }}
                  >
                    Copilot
                  </th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw) => {
                  const snap = kw.latestSnapshot;
                  const kd = difficultyMap.get(kw.keyword.toLowerCase());
                  const session = copilotByKeyword.get(kw.id);
                  return (
                    <tr key={kw.id} style={{ borderBottom: '1px solid hsla(0,0%,0%,0.04)' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <Link
                          to={`${basePath}/keywords/${kw.id}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ '&:hover': { color: '#054AD9' } }}
                          >
                            {kw.keyword}
                          </Typography>
                        </Link>
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        {snap?.position != null ? (
                          <Chip
                            label={`#${snap.position.toFixed(1)}`}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: 11,
                              color: '#fff',
                              backgroundColor: positionColor(snap.position),
                              minWidth: 44,
                              borderRadius: '8px',
                            }}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        {kd?.difficulty != null ? (
                          <Chip
                            label={kd.difficulty}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: 11,
                              color: '#fff',
                              backgroundColor: kdColor(kd.difficulty),
                              minWidth: 36,
                              borderRadius: '8px',
                            }}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <Typography variant="caption">{snap?.clicks ?? '—'}</Typography>
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <Typography variant="caption">{snap?.impressions ?? '—'}</Typography>
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        <Typography variant="caption">
                          {snap?.ctr != null ? `${(snap.ctr * 100).toFixed(1)}%` : '—'}
                        </Typography>
                      </td>
                      <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                        {session ? (
                          <Chip
                            label={session.status}
                            size="small"
                            color={
                              session.status === 'active'
                                ? 'success'
                                : session.status === 'paused'
                                ? 'warning'
                                : session.status === 'completed'
                                ? 'info'
                                : 'default'
                            }
                            sx={{ fontSize: 10, height: 20, textTransform: 'capitalize' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            Inactive
                          </Typography>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      {/* Service Page Optimization */}
      {isServicePage && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Typography
              variant="overline"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1.5 }}
            >
              Service Page Optimization
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={
                isCheckingHealth ? (
                  <CircularProgress size={12} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 14 }} />
                )
              }
              onClick={onCheckHealth}
              disabled={isCheckingHealth}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: 11, color: '#71717A' }}
            >
              {isCheckingHealth ? 'Checking...' : 'Re-check all'}
            </Button>
          </Box>

          {!healthResult && isCheckingHealth && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ color: '#054AD9' }} />
            </Box>
          )}

          {healthResult && (
            <>
              {/* Progress summary */}
              {(() => {
                const checks = Object.values(healthResult.checks);
                const passed = checks.filter((c) => c.pass === true).length;
                const failed = checks.filter((c) => c.pass === false).length;
                const total = checks.length;
                return (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 1.5,
                      backgroundColor: '#F4F4F5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        {checks.map((c, i) => (
                          <Box
                            key={i}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor:
                                c.pass === true
                                  ? '#0D9D58'
                                  : c.pass === false
                                  ? '#EA4335'
                                  : '#F4B400',
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {passed}/{total} checks passing
                        {failed > 0
                          ? ` — ${failed} item${failed > 1 ? 's' : ''} need${
                              failed === 1 ? 's' : ''
                            } attention`
                          : ' — all good'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}

              {/* FAQ Schema — dedicated visual section with preview workflow */}
              {(() => {
                const faqCheck = healthResult.checks.faqSchema;
                const existingFaqs = healthResult.existingFaqs ?? [];
                const hasFaqs = existingFaqs.length > 0;
                const hasPreview = generatedFaqs && generatedFaqs.length > 0;
                const displayFaqs = hasPreview ? generatedFaqs : existingFaqs;

                return (
                  <Box
                    sx={{
                      ...sectionSx,
                      mb: 2,
                      borderColor:
                        faqCheck.pass === true
                          ? '#0D9D5830'
                          : hasPreview
                          ? '#054AD930'
                          : '#EA433520',
                      borderWidth: '2px',
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <QuizIcon
                        sx={{
                          fontSize: 22,
                          color: faqCheck.pass ? '#0D9D58' : hasPreview ? '#054AD9' : '#EA4335',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          sx={{ fontSize: 15, lineHeight: 1.3 }}
                        >
                          FAQ Schema Markup
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          {hasPreview
                            ? 'Review the generated FAQs below, then publish to WordPress'
                            : faqCheck.detail}
                        </Typography>
                      </Box>
                      {hasPreview ? (
                        <Chip
                          label="Preview"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: 11,
                            backgroundColor: '#054AD915',
                            color: '#054AD9',
                          }}
                        />
                      ) : (
                        <Chip
                          label={faqCheck.pass ? `${existingFaqs.length} FAQs` : 'Needs Fix'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: 11,
                            backgroundColor: faqCheck.pass ? '#0D9D5815' : '#EA433515',
                            color: faqCheck.pass ? '#0D9D58' : '#EA4335',
                          }}
                        />
                      )}
                    </Box>

                    {/* Preview banner */}
                    {hasPreview && (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          backgroundColor: '#054AD908',
                          border: '1px dashed #054AD940',
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#054AD9"
                          sx={{ fontSize: 13, mb: 0.5 }}
                        >
                          {generatedFaqs!.length} FAQs generated — review below and publish when
                          ready
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          These FAQs will be added as FAQPage JSON-LD schema to your WordPress page.
                          You can review each one before publishing.
                        </Typography>
                      </Box>
                    )}

                    {/* FAQ accordion — shows existing or preview */}
                    {displayFaqs.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {displayFaqs.map((faq, i) => (
                          <Box
                            key={i}
                            sx={{
                              border: '1px solid',
                              borderColor: hasPreview ? '#054AD920' : 'divider',
                              borderRadius: 1.5,
                              mb: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                              sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                backgroundColor: hasPreview ? '#054AD905' : '#FAFAFA',
                                '&:hover': {
                                  backgroundColor: hasPreview ? '#054AD910' : '#F4F4F5',
                                },
                                '&:active': {
                                  backgroundColor: hasPreview ? '#054AD915' : '#EEEEEE',
                                },
                                transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                  backgroundColor: hasPreview ? '#054AD9' : '#71717A',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 11,
                                  fontWeight: 700,
                                }}
                              >
                                {i + 1}
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ flex: 1, fontSize: 13 }}
                              >
                                {faq.question}
                              </Typography>
                              {expandedFaq === i ? (
                                <ExpandLessIcon sx={{ fontSize: 18, color: '#71717A' }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 18, color: '#71717A' }} />
                              )}
                            </Box>
                            {expandedFaq === i && (
                              <Box
                                sx={{
                                  p: 2,
                                  borderTop: '1px solid',
                                  borderColor: hasPreview ? '#054AD920' : 'divider',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: 13, lineHeight: 1.8 }}
                                >
                                  {faq.answer}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* No FAQs explanation */}
                    {!hasFaqs && !hasPreview && !faqCheck.pass && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ p: 2, borderRadius: 1.5, backgroundColor: '#FFF5F5', mb: 2 }}>
                          <Typography variant="body2" sx={{ fontSize: 13 }}>
                            No FAQ schema found. Competitors like Arc.dev show 11 FAQs with rich
                            results in Google, taking up more SERP space and increasing CTR by
                            20-30%.
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 2,
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: '#054AD908',
                          }}
                        >
                          <RocketLaunchIcon sx={{ fontSize: 14, color: '#054AD9', mt: 0.25 }} />
                          <Typography
                            variant="caption"
                            color="#054AD9"
                            sx={{ fontSize: 11, fontWeight: 500 }}
                          >
                            Generate 8-10 fintech-focused FAQs with complete FAQPage JSON-LD schema.
                            Review before publishing.
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      {/* Generate button — shown when no preview exists */}
                      {!hasPreview && (
                        <Button
                          variant={faqCheck.pass ? 'outlined' : 'contained'}
                          startIcon={
                            isGeneratingFaqs ? (
                              <CircularProgress
                                size={14}
                                sx={{ color: faqCheck.pass ? '#054AD9' : '#fff' }}
                              />
                            ) : (
                              <QuizIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          onClick={onGenerateFaqs}
                          disabled={isGeneratingFaqs}
                          sx={{
                            backgroundColor: faqCheck.pass ? undefined : '#054AD9',
                            borderColor: faqCheck.pass ? '#054AD9' : undefined,
                            color: faqCheck.pass ? '#054AD9' : undefined,
                            '&:hover': { backgroundColor: faqCheck.pass ? '#054AD910' : '#0440B8' },
                            textTransform: 'none',
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          {isGeneratingFaqs
                            ? 'Generating FAQs...'
                            : hasFaqs
                            ? 'Regenerate FAQs'
                            : 'Generate FAQs'}
                        </Button>
                      )}

                      {/* Preview actions — shown when preview exists */}
                      {hasPreview && (
                        <>
                          <Button
                            variant="contained"
                            startIcon={
                              isPublishingFaqs ? (
                                <CircularProgress size={14} sx={{ color: '#fff' }} />
                              ) : (
                                <RocketLaunchIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            onClick={onPublishFaqs}
                            disabled={isPublishingFaqs}
                            sx={{
                              backgroundColor: '#0D9D58',
                              '&:hover': { backgroundColor: '#0B8A4B' },
                              textTransform: 'none',
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            {isPublishingFaqs ? 'Publishing...' : 'Publish to WordPress'}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={onGenerateFaqs}
                            disabled={isGeneratingFaqs}
                            sx={{
                              textTransform: 'none',
                              fontSize: 13,
                              borderColor: '#054AD9',
                              color: '#054AD9',
                            }}
                          >
                            {isGeneratingFaqs ? 'Regenerating...' : 'Regenerate'}
                          </Button>
                          <Button
                            variant="text"
                            onClick={onClearFaqPreview}
                            sx={{ textTransform: 'none', fontSize: 13, color: '#71717A' }}
                          >
                            Discard
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })()}

              {/* Other check sections (skip faqSchema — rendered above) */}
              {Object.entries(healthResult.checks)
                .filter(([key]) => key !== 'faqSchema')
                .map(([key, check]) => {
                  const config: Record<
                    string,
                    { name: string; description: string; impact: string; fixLabel: string }
                  > = {
                    faqSchema: {
                      name: 'FAQ Schema Markup',
                      description:
                        'FAQPage structured data tells Google your page has Q&A content, enabling rich FAQ snippets directly in search results. Competitors like Arc.dev show 11 FAQs in rich results.',
                      impact:
                        'Enables FAQ rich results in Google — increases SERP visibility and click-through rate by 20-30%.',
                      fixLabel: 'Generate FAQs + Schema',
                    },
                    serviceSchema: {
                      name: 'Product / Service Schema',
                      description:
                        'Product schema with AggregateOffer and AggregateRating enables star ratings and pricing to appear in search results. Lemon.io shows 4.9 stars and $65-85/hr in SERPs using this exact pattern.',
                      impact:
                        'Star ratings in search results increase CTR by 25-35%. Pricing visibility attracts qualified leads.',
                      fixLabel: 'Generate Product Schema',
                    },
                    pricing: {
                      name: 'Pricing & Rate Information',
                      description:
                        'Searchers for "hire X developers" have commercial intent — they want to know costs before clicking. Arc.dev shows "$60-100+/hr" and Lemon.io shows "$25-45/hr" directly on their pages.',
                      impact:
                        'Pages with pricing data rank higher for commercial keywords and convert better. Enables Product schema with price range.',
                      fixLabel: 'Generate Pricing Section',
                    },
                    titleFreshness: {
                      name: 'Title Freshness (Current Month)',
                      description:
                        'Competitors like Arc.dev update their title every month to include the current month and year (e.g., "April 2026"). Google rewards freshness signals for commercial "hire" queries.',
                      impact:
                        'Monthly title updates signal fresh content to Google. Pages with current dates get higher CTR in search results.',
                      fixLabel: 'Update Title with Current Month',
                    },
                    contentDepth: {
                      name: 'Content Depth',
                      description:
                        'Top-ranking service pages have 4,000-5,000+ words covering: vetting process, skills matrix, pricing, staff augmentation comparisons, technology-specific use cases, and FAQs.',
                      impact:
                        'Deeper content ranks for more long-tail keywords and keeps visitors on the page longer — both direct ranking signals.',
                      fixLabel: 'Generate Content Sections',
                    },
                    uniqueness: {
                      name: 'Template Uniqueness',
                      description:
                        "When multiple service pages share the same template content (testimonials, process, etc.), Google may treat them as near-duplicates. Each page needs technology-specific content that can't be reused on sibling pages.",
                      impact:
                        'Unique content per page prevents cannibalization and helps each page rank for its target keyword independently.',
                      fixLabel: 'Generate Unique Content',
                    },
                  };

                  const cfg = config[key] ?? {
                    name: key,
                    description: '',
                    impact: '',
                    fixLabel: 'Fix',
                  };
                  const isFixing = fixingCheck === key;
                  const canFix = check.pass === false && keywords.length > 0;
                  const isPassing = check.pass === true;
                  const isUnknown = check.pass === null;

                  return (
                    <Box
                      key={key}
                      sx={{
                        ...sectionSx,
                        mb: 2,
                        borderColor: isPassing
                          ? '#0D9D5830'
                          : check.pass === false
                          ? '#EA433520'
                          : 'divider',
                        borderWidth: isPassing || check.pass === false ? '2px' : '1px',
                      }}
                    >
                      {/* Header row */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: isPassing ? 0 : 2,
                        }}
                      >
                        {isPassing ? (
                          <CheckCircleIcon sx={{ fontSize: 22, color: '#0D9D58' }} />
                        ) : check.pass === false ? (
                          <CancelIcon sx={{ fontSize: 22, color: '#EA4335' }} />
                        ) : (
                          <HelpOutlineIcon sx={{ fontSize: 22, color: '#F4B400' }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{ fontSize: 15, lineHeight: 1.3 }}
                          >
                            {cfg.name}
                          </Typography>
                          {isPassing && (
                            <Typography variant="caption" color="#0D9D58" sx={{ fontSize: 11 }}>
                              {check.detail}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={isPassing ? 'Passing' : isUnknown ? 'Unknown' : 'Needs Fix'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: 11,
                            backgroundColor: isPassing
                              ? '#0D9D5815'
                              : isUnknown
                              ? '#F4B40015'
                              : '#EA433515',
                            color: isPassing ? '#0D9D58' : isUnknown ? '#F4B400' : '#EA4335',
                          }}
                        />
                      </Box>

                      {/* Detail content — only show for failing/unknown checks */}
                      {!isPassing && (
                        <>
                          {/* What's wrong */}
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 1.5,
                              backgroundColor: check.pass === false ? '#FFF5F5' : '#FFFBF0',
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                              sx={{
                                fontSize: 10,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                mb: 0.5,
                                display: 'block',
                              }}
                            >
                              Current Status
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 13 }}>
                              {check.detail}
                            </Typography>
                          </Box>

                          {/* Why it matters */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: 12, display: 'block', mb: 1.5, lineHeight: 1.6 }}
                          >
                            {cfg.description}
                          </Typography>

                          {/* Impact */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: 2,
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: '#054AD908',
                            }}
                          >
                            <RocketLaunchIcon sx={{ fontSize: 14, color: '#054AD9', mt: 0.25 }} />
                            <Typography
                              variant="caption"
                              color="#054AD9"
                              sx={{ fontSize: 11, fontWeight: 500 }}
                            >
                              {cfg.impact}
                            </Typography>
                          </Box>

                          {/* Fix button */}
                          {canFix && (
                            <Button
                              variant="contained"
                              startIcon={
                                isFixing ? (
                                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                                ) : (
                                  <RocketLaunchIcon sx={{ fontSize: 16 }} />
                                )
                              }
                              onClick={() => onFixCheck(key)}
                              disabled={isFixing || !!fixingCheck}
                              sx={{
                                backgroundColor: '#054AD9',
                                '&:hover': { backgroundColor: '#0440B8' },
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 500,
                              }}
                            >
                              {isFixing ? 'Generating fix...' : cfg.fixLabel}
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  );
                })}
            </>
          )}
        </>
      )}

      {/* Backlink Strength */}
      {ahrefsDomainSnapshot && (
        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LinkIcon sx={{ fontSize: 20, color: '#054AD9' }} />
            <Typography
              variant="overline"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1.5 }}
            >
              Backlink Strength
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: 10 }}>
              via Ahrefs
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Domain Rating
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#054AD9">
                {ahrefsDomainSnapshot.domainRating ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                / 100
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Referring Domains
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#4285F4">
                {ahrefsDomainSnapshot.refdomainsLive != null
                  ? fmtNum(ahrefsDomainSnapshot.refdomainsLive)
                  : '—'}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Backlinks
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#A142F4">
                {ahrefsDomainSnapshot.backlinksLive != null
                  ? fmtNum(ahrefsDomainSnapshot.backlinksLive)
                  : '—'}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: ahrefsNewBacklinks > 0 ? '#0D9D5830' : 'divider',
                textAlign: 'center',
                backgroundColor: ahrefsNewBacklinks > 0 ? '#F0FFF4' : undefined,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                New <TrendingUpIcon sx={{ fontSize: 12, color: '#0D9D58' }} />
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#0D9D58">
                +{ahrefsNewBacklinks}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                last 7 days
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 120,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: ahrefsLostBacklinks > 3 ? '#EA433530' : 'divider',
                textAlign: 'center',
                backgroundColor: ahrefsLostBacklinks > 3 ? '#FFF5F5' : undefined,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                Lost <TrendingDownIcon sx={{ fontSize: 12, color: '#EA4335' }} />
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                color={ahrefsLostBacklinks > 3 ? '#EA4335' : '#96979b'}
              >
                {ahrefsLostBacklinks > 0 ? `-${ahrefsLostBacklinks}` : '0'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                last 7 days
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Applied Changes */}
      {appliedChanges.length > 0 && (
        <Box sx={sectionSx}>
          <Typography
            variant="overline"
            fontWeight={700}
            color="text.secondary"
            sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}
          >
            Applied Changes ({appliedChanges.length})
          </Typography>
          <ChangeImpactTimeline changes={appliedChanges} />
        </Box>
      )}
    </Box>
  );
};

export default SeoPageDetailComponent;
